/**
 * Action items — "the spine of this build". They drive the §8e reminder emails
 * and two of the seven stall predicates.
 *
 * The pieces here are shared by the Actions page, the opportunity's Actions
 * tab, the account 360 and the meeting form, because a commitment captured in
 * one place has to look and behave the same in the others.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, Plus } from 'lucide-react';
import { ACTION_STATUSES, OPEN_ACTION_STATUSES, PRIORITIES } from '@menlo/shared';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { useApiMutation, useApiQuery, type Paged, type Row } from '@/lib/queries';
import { useUserLookup } from '@/lib/reference';
import { useToast } from '@/lib/toast';
import { addDays, daysSince, formatDate, humanize, today } from '@/lib/format';
import { cn } from '@/lib/utils';

export const ACTIONS_QUERY_KEY = ['action-items'] as const;

/**
 * Every query an action-item write makes stale.
 *
 * `['action-items']` alone is not enough: the opportunity's Actions tab, its
 * count and its timeline are read from the opportunity context
 * (`['opportunity', id]`), and the account 360's open items from
 * `['account-summary', id]`. Invalidating only the list made *Add action*
 * look as if it did nothing until a refocus (docs/bugs.md, 2026-10-10
 * session 1). The prefixes are invalidated whole rather than by id because an
 * item shown on an account page may carry only its opportunity's id, and only
 * the queries actually mounted refetch.
 */
export const ACTION_WRITE_INVALIDATES = [
  ACTIONS_QUERY_KEY,
  ['opportunity'],
  ['account-summary'],
] as const;

export function isOpen(item: Row): boolean {
  return (OPEN_ACTION_STATUSES as readonly string[]).includes(String(item['status']));
}

export type Bucket = 'overdue' | 'today' | 'week' | 'later' | 'nodate';

export function bucketOf(item: Row): Bucket {
  const due = item['due_date'] as string | null;
  if (due === null || due === undefined) return 'nodate';
  const days = daysSince(due);
  if (days === null) return 'nodate';
  if (days > 0) return 'overdue';
  if (days === 0) return 'today';
  return days >= -7 ? 'week' : 'later';
}

export const BUCKET_LABELS: Record<Bucket, string> = {
  overdue: 'Overdue',
  today: 'Due today',
  week: 'This week',
  later: 'Later',
  nodate: 'No due date',
};

export function ActionItemRow({
  item,
  canWrite,
  showOpportunity = true,
  blockers,
}: {
  item: Row;
  canWrite: boolean;
  showOpportunity?: boolean;
  blockers?: Map<string, Row>;
}): React.ReactElement {
  const users = useUserLookup();
  const [completing, setCompleting] = useState(false);

  const blockedById = item['blocked_by_action_id'] as string | null;
  const blocker =
    blockedById === null || blockers === undefined ? undefined : blockers.get(blockedById);
  const blocked = blocker !== undefined && isOpen(blocker);

  const due = item['due_date'] as string | null;
  const overdue = bucketOf(item) === 'overdue' && isOpen(item);
  const customerOwned = item['owner_type'] === 'customer';

  return (
    <>
      <div className="flex items-start gap-2 border-b px-2 py-2 last:border-b-0">
        <Checkbox
          aria-label={`Complete ${String(item['title'])}`}
          checked={item['status'] === 'done'}
          disabled={!canWrite || item['status'] === 'done'}
          onChange={() => setCompleting(true)}
        />

        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'text-sm',
              item['status'] === 'done' && 'text-muted-foreground line-through',
            )}
          >
            {String(item['title'])}
          </p>

          <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
            {due !== null && (
              <span className={cn(overdue && 'font-medium text-red-700')}>
                {overdue ? 'Overdue — ' : 'Due '}
                {formatDate(due)}
              </span>
            )}
            {customerOwned ? (
              <Badge variant="info">Waiting on customer</Badge>
            ) : (
              <span>{users.get(String(item['owner_user_id']))?.display_name ?? 'Unassigned'}</span>
            )}
            {item['priority'] !== 'normal' && (
              <Badge variant="outline">{humanize(String(item['priority']))}</Badge>
            )}
            {blocked && (
              <Badge variant="risk">
                <Ban className="h-3 w-3" aria-hidden="true" />
                Blocked by &ldquo;{String(blocker?.['title'])}&rdquo;
              </Badge>
            )}
            {showOpportunity &&
              item['opportunity_id'] !== null &&
              item['opportunity_id'] !== undefined && (
                <Link
                  to={`/opportunities/${String(item['opportunity_id'])}?tab=actions`}
                  className="underline underline-offset-2"
                >
                  Open the deal
                </Link>
              )}
          </p>
        </div>
      </div>

      {completing && <CompleteActionDialog item={item} onClose={() => setCompleting(false)} />}
    </>
  );
}

/**
 * Completing prompts for an optional note and **offers to create the follow-on
 * action**, because the next commitment is decided at the moment the last one
 * is finished and nowhere else.
 */
function CompleteActionDialog({
  item,
  onClose,
}: {
  item: Row;
  onClose: () => void;
}): React.ReactElement {
  const [note, setNote] = useState('');
  const [followOn, setFollowOn] = useState('');
  const [followOnDue, setFollowOnDue] = useState(addDays(today(), 10));

  const complete = useApiMutation<Record<string, unknown>, void>(
    () => ({
      path: `/api/action-items/${String(item['id'])}`,
      method: 'PUT',
      body: {
        status: 'done',
        ...(note.trim() === '' ? {} : { completion_note: note.trim() }),
      },
    }),
    { invalidate: ACTION_WRITE_INVALIDATES },
  );

  const createFollowOn = useApiMutation<Record<string, unknown>, void>(
    () => ({
      path: '/api/action-items',
      method: 'POST',
      body: {
        title: followOn.trim(),
        due_date: followOnDue,
        owner_type: item['owner_type'] ?? 'internal',
        ...(item['owner_user_id'] ? { owner_user_id: item['owner_user_id'] } : {}),
        ...(item['owner_contact_id'] ? { owner_contact_id: item['owner_contact_id'] } : {}),
        ...(item['opportunity_id'] ? { opportunity_id: item['opportunity_id'] } : {}),
        ...(item['account_id'] ? { account_id: item['account_id'] } : {}),
      },
    }),
    { invalidate: ACTION_WRITE_INVALIDATES },
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title="Mark it done"
      description={String(item['title'])}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await complete.mutateAsync();
              if (followOn.trim() !== '') await createFollowOn.mutateAsync();
              onClose();
            }}
            disabled={complete.isPending}
          >
            Done
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Completion note (optional)">
          <Textarea value={note} onChange={(event) => setNote(event.target.value)} rows={3} />
        </Field>
        <fieldset className="rounded-md border p-3">
          <legend className="px-1 text-xs font-medium">Follow-on action</legend>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Title" className="col-span-2">
              <Input
                value={followOn}
                placeholder="What happens next?"
                onChange={(event) => setFollowOn(event.target.value)}
              />
            </Field>
            <Field label="Due">
              <Input
                type="date"
                value={followOnDue}
                onChange={(event) => setFollowOnDue(event.target.value)}
              />
            </Field>
          </div>
        </fieldset>
      </div>
    </Dialog>
  );
}

export interface QuickAddContext {
  opportunityId?: string | null;
  accountId?: string | null;
  meetingId?: string | null;
  /** Shown so the user can see what the item will hang off. */
  label?: string;
}

/**
 * Quick-add. "Follow up in 10 days" must be two keystrokes and a date chip,
 * because that is the single most common thing anyone will do in this system.
 */
export function QuickAddDialog({
  context,
  onClose,
}: {
  context: QuickAddContext;
  onClose: () => void;
}): React.ReactElement {
  const users = useUserLookup();
  const { push } = useToast();
  const [title, setTitle] = useState('');
  const [due, setDue] = useState(addDays(today(), 10));
  const [ownerType, setOwnerType] = useState<'internal' | 'customer'>('internal');
  const [ownerUserId, setOwnerUserId] = useState('');
  const [priority, setPriority] = useState('normal');

  const create = useApiMutation<Record<string, unknown>, void>(
    () => ({
      path: '/api/action-items',
      method: 'POST',
      body: {
        title: title.trim(),
        due_date: due,
        priority,
        owner_type: ownerType,
        ...(ownerType === 'internal' && ownerUserId !== '' ? { owner_user_id: ownerUserId } : {}),
        ...(context.opportunityId ? { opportunity_id: context.opportunityId } : {}),
        ...(context.accountId ? { account_id: context.accountId } : {}),
        ...(context.meetingId ? { meeting_id: context.meetingId } : {}),
      },
    }),
    {
      invalidate: ACTION_WRITE_INVALIDATES,
      onSuccess: () => {
        push({ tone: 'success', title: 'Action added' });
        onClose();
      },
    },
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title="New action"
      description={context.label ?? 'Not linked to an opportunity — it still reaches your digest.'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => create.mutate()}
            disabled={title.trim() === '' || create.isPending}
          >
            Add
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="What needs doing?">
          <Input
            autoFocus
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && title.trim() !== '') create.mutate();
            }}
          />
        </Field>

        <div className="flex flex-wrap items-center gap-1.5">
          {[3, 7, 10, 30].map((days) => (
            <button
              key={days}
              type="button"
              onClick={() => setDue(addDays(today(), days))}
              className={cn(
                'rounded-full border px-2 py-0.5 text-xs',
                due === addDays(today(), days)
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-input hover:bg-accent',
              )}
            >
              In {days} days
            </button>
          ))}
          <Input
            type="date"
            aria-label="Due date"
            className="h-7 w-36 text-xs"
            value={due}
            onChange={(event) => setDue(event.target.value)}
          />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Field label="Owned by">
            <Select
              value={ownerType}
              onChange={(event) => setOwnerType(event.target.value as 'internal' | 'customer')}
            >
              <option value="internal">Us</option>
              <option value="customer">The customer</option>
            </Select>
          </Field>
          {ownerType === 'internal' && (
            <Field label="Who">
              <Select value={ownerUserId} onChange={(event) => setOwnerUserId(event.target.value)}>
                <option value="">Me</option>
                {[...users.values()].map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.display_name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label="Priority">
            <Select value={priority} onChange={(event) => setPriority(event.target.value)}>
              {PRIORITIES.map((value) => (
                <option key={value} value={value}>
                  {humanize(value)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <p className="text-[11px] text-muted-foreground">
          Assigning this to somebody else emails them once — the identifier and a link, never the
          title or the customer.
        </p>
      </div>
    </Dialog>
  );
}

/** The keyboard shortcut behind quick-add. */
export function useQuickAdd(context: QuickAddContext): {
  open: boolean;
  setOpen: (open: boolean) => void;
  button: React.ReactElement;
  dialog: React.ReactNode;
} {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      const typing = target !== null && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
      if (!typing && event.key === 'a' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return {
    open,
    setOpen,
    button: (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        Add action
      </Button>
    ),
    dialog: open ? <QuickAddDialog context={context} onClose={() => setOpen(false)} /> : null,
  };
}

/** Items for one entity, used by the opportunity Actions tab and the account 360. */
export function useActionItems(params: {
  opportunityId?: string;
  accountId?: string;
  ownerId?: string;
}): { items: Row[]; isLoading: boolean } {
  const query = new URLSearchParams({ pageSize: '200', sort: 'due_date' });
  if (params.opportunityId !== undefined) query.set('opportunityId', params.opportunityId);
  if (params.accountId !== undefined) query.set('accountId', params.accountId);
  if (params.ownerId !== undefined) query.set('ownerId', params.ownerId);

  const result = useApiQuery<Paged<Row>>(
    [...ACTIONS_QUERY_KEY, query.toString()],
    `/api/action-items?${query.toString()}`,
  );
  return { items: result.data?.items ?? [], isLoading: result.isLoading };
}

export { ACTION_STATUSES };
